import { clone, equals } from 'ramda';
import { ucFirst } from './helpers';

import PropTypes from 'prop-types';

const EVENTS = [
  'focusin',
  'focusout',
  'click',
  'dblclick',
  'mousedown',
  'mouseup',
  'mousemove',
  'mouseover',
  'beforepaste',
  'paste',
  'cut',
  'copy',
  'selectionchange',
  'mouseout',
  'mouseenter',
  'mouseleave',
  'keydown',
  'keypress',
  'keyup',
  'contextmenu',
  'dragend',
  'dragover',
  'draggesture',
  'dragdrop',
  'drop',
  'drag',
  'BeforeRenderUI',
  'SetAttrib',
  'PreInit',
  'PostRender',
  'init',
  'deactivate',
  'activate',
  'NodeChange',
  'BeforeExecCommand',
  'ExecCommand',
  'show',
  'hide',
  'ProgressState',
  'LoadContent',
  'SaveContent',
  'BeforeSetContent',
  'SetContent',
  'BeforeGetContent',
  'GetContent',
  'VisualAid',
  'remove',
  'submit',
  'reset',
  'BeforeAddUndo',
  'AddUndo',
  'change',
  'undo',
  'redo',
  'ClearUndos',
  'ObjectSelected',
  'ObjectResizeStart',
  'ObjectResized',
  'PreProcess',
  'PostProcess',
  'focus',
  'blur',
  'dirty',
];

// Note: because the capitalization of the events is weird, we're going to get
// some inconsistently-named handlers, for example compare:
// 'onMouseleave' and 'onNodeChange'
const HANDLER_NAMES = EVENTS.map(type => {
  return 'on' + ucFirst(type);
});

export default class TinyMCE extends React.PureComponent {
  constructor(props) {
    super(props);

    this.id = props.id;
    this.autoFocus = props.autoFocus;
  }

  static propTypes = {
    autoFocus: PropTypes.bool,
    id: PropTypes.string.isRequired,
    className: PropTypes.string,
    config: PropTypes.shape({
      plugins: PropTypes.array,
      toolbar: PropTypes.string,
      menu: PropTypes.object,
      height: PropTypes.number,
      entity_encoding: PropTypes.string,
      relative_urls: PropTypes.bool,
      remove_script_host: PropTypes.bool,
      convert_urls: PropTypes.bool,
    }),
    content: PropTypes.string.isRequired,
    name: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onKeyUp: PropTypes.func,
    onClick: PropTypes.func,
  };

  static defaultProps = {
    autoFocus: false,
    config: {},
    content: '',
  };

  componentDidMount() {
    const config = clone(this.props.config);
    this._init(config, this.props.content);
  }

  componentWillReceiveProps(nextProps) {
    if (!equals(this.props.config, nextProps.config) || !equals(this.props.id, nextProps.id)) {
      this.id = nextProps.id;
      this._init(clone(nextProps.config), nextProps.content);
      return;
    }

    // TODO: This will fix problems with keeping internal state, but causes problems with cursor placement
    // if (!equals(this.props.content, nextProps.content)) {
    //   const editor = tinymce.EditorManager.get(this.id);
    //   editor.setContent(nextProps.content);
    //
    //   editor.selection.select(editor.getBody(), true);
    //   editor.selection.collapse(false);
    // }
  }

  componentWillUnmount() {
    this._remove();
  }

  _init(config, content) {
    if (this._isInit) {
      this._remove();
    }

    // hide the textarea that is me so that no one sees it
    reactDOM.findDOMNode(this).style.hidden = 'hidden';

    const setupCallback = config.setup;
    const hasSetupCallback = typeof setupCallback === 'function';

    config.selector = `#${this.id}`;
    config.setup = editor => {
      EVENTS.forEach((eventType, index) => {
        editor.on(eventType, event => {
          if (event.ctrlKey && (event.keyCode === 10 || event.keyCode === 13)) {
            event.preventDefault();
          }
          const handler = this.props[HANDLER_NAMES[index]];
          if (typeof handler === 'function') {
            // native DOM events don't have access to the editor so we pass it here
            handler(event, editor);
          }
        });
      });
      // editor.shortcuts.add('ctrl + 13', 'submit', 'submit');

      // need to set content here because the textarea will still have the
      // old `this.props.content`
      if (typeof content !== 'undefined') {
        editor.on('init', () => {
          editor.setContent(content);
          if (this.autoFocus) {
            editor.focus();
          }
        });
      }
      if (hasSetupCallback) {
        setupCallback(editor);
      }
    };

    window.tinymce.init(config);

    reactDOM.findDOMNode(this).style.hidden = '';

    this._isInit = true;
  }

  _remove() {
    window.tinymce.EditorManager.execCommand('mceRemoveEditor', true, this.id);
    this._isInit = false;
  }

  getField() {
    return this.refs.field;
  }

  render() {
    return this.props.config.inline ? (
      <div id={this.id} className={this.props.className} dangerouslySetInnerHTML={{ __html: this.props.content }} />
    ) : (
      <textarea
        autoFocus={Boolean(this.props.autoFocus)}
        id={this.id}
        className={this.props.className}
        defaultValue={this.props.content}
        name={this.props.name || this.props.id}
        ref="field"
        style={{
          minHeight: this.props.config.height,
        }}
      />
    );
  }
}
